<template>
  <div>
    <div
      class="inline-flex items-center"
      role="button"
      tabindex="0"
      @click.stop="modal = true"
    >
      <slot name="trigger" />
    </div>

    <b-modal
      v-model="modal"
      dialog-class="!max-w-[880px] rounded-[5px]"
      body-class="!py-8 !px-12"
      hide-footer
      hide-header
      centered
      lazy
    >
      <i
        class="fa fa-close z-[50] absolute top-[15px] right-[17px] text-[16px] text-[#6E747A] transition cursor-pointer hover:opacity-60"
        @click="modal = false"
      />

      <template>
        <b-form class="w-full flex flex-col items-center">
          <template v-if="!loading">
            <h4
              class="text-center text-[27px] font-semibold"
            >
              {{ currentTemplate ? 'Edit Template' : 'New Template' }}
            </h4>

            <b-row
              v-if="!loading"
              class="w-full mt-14"
            >
              <b-col
                v-if="!onlyAccess"
                cols="4"
              >
                <div
                  v-if="!(form.thumbnail.default ? form.thumbnail.screenshot : form.thumbnail.source)"
                  class="w-full bg-[#D9D9D9] border border-[#E2E5EC] rounded-[5px] bg-cover aspect-square"
                />
                <img
                  v-else
                  :src="form.thumbnail.default ? form.thumbnail.screenshot : form.thumbnail.source"
                  alt="Template Thumbnail"
                  class="w-full bg-[#D9D9D9] border border-[#E2E5EC] rounded-[5px] object-cover aspect-square"
                >

                <b-form-checkbox
                  v-model="form.thumbnail.default"
                  name="thumbnail"
                  switch
                >
                  <div class="text-wrap text-[#44474A] translate-y-[-.4rem] mb-0">
                    Default Thumbnail
                  </div>
                </b-form-checkbox>

                <upload-asset
                  type="jpg|png|jpeg|blob|webp"
                  @uploaded="e => insertImage(e.url)"
                >
                  <template #trigger>
                    <div class="inline-flex items-center mt-[12px]">
                      <label class="cursor-pointer inline-flex items-center">
                        <svg
                          class="mr-2 w-4 h-4"
                          width="14"
                          height="12"
                          viewBox="0 0 14 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.16667 8.47463C1.46317 8.00373 1 7.20179 1 6.29167C1 4.92458 2.04505 3.80159 3.37985 3.67797C3.65289 2.01708 5.09514 0.75 6.83333 0.75C8.57152 0.75 10.0138 2.01708 10.2868 3.67797C11.6216 3.80159 12.6667 4.92458 12.6667 6.29167C12.6667 7.20179 12.2035 8.00373 11.5 8.47463M4.5 8.33333L6.83333 6M6.83333 6L9.16667 8.33333M6.83333 6V11.25"
                            stroke="black"
                            stroke-width="1.3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>

                        <span class="text-[#44474A]">
                          Upload Thumbnail
                        </span>
                      </label>
                    </div>
                  </template>
                </upload-asset>
              </b-col>
              <b-col :cols="onlyAccess ? 12 : 8">
                <b-form-group label="Template Name">
                  <b-input
                    v-model="form.name"
                    name="name"
                  />
                </b-form-group>

                <b-form-group label="Description">
                  <b-textarea
                    v-model="form.description"
                    name="description"
                    class=""
                    rows="5"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row
              v-if="!onlyAccess"
              class="w-full mt-10"
            >
              <b-col cols="6">
                <label class="text-[15px] text-[#3E3E3E] mb-[12px]">
                  Categories
                </label>

                <div class="relative flex items-center gap-[10px]">
                  <v-select
                    v-model="form.categories"
                    :options="categories"
                    class="multi-select !rounded-[4px] border border-[#E3E3E3]"
                    placeholder="Select Categories"
                    :searchable="false"
                    :indicator="false"
                    label="name"
                    :reduce="item => item.id"
                    multiple
                  />

                  <button
                    class="absolute right-[10px] top-[10px] !w-[20px] !h-[20px] hover:opacity-80 p-0"
                    type="button"
                    @click="categoryModal = true"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.2606 2.8514L11.0411 5.0709L9.09961 4.37733L8.40608 2.43587L10.6256 0.216373C9.16938 -0.270789 7.49841 0.0644958 6.33886 1.22409C5.20071 2.36224 4.85681 3.99293 5.30499 5.42977C5.24463 5.47728 5.18615 5.52844 5.13046 5.58413L0.572138 10.1424C-0.190713 10.9052 -0.190713 12.142 0.572138 12.9048C1.33499 13.6677 2.57181 13.6677 3.33466 12.9048L7.89298 8.34652C7.94867 8.29083 7.99974 8.23239 8.04731 8.17204C9.48415 8.62021 11.1148 8.27627 12.2529 7.13812C13.4124 5.97852 13.7478 4.30765 13.2606 2.8514Z"
                        fill="#6E747A"
                      />
                    </svg>
                  </button>
                </div>
              </b-col>
              <b-col cols="6">
                <label class="text-[15px] text-[#3E3E3E] mb-[12px]">
                  Themes
                </label>

                <div class="relative flex items-center gap-[10px]">
                  <v-select
                    v-model="form.themes"
                    :options="themes"
                    class="multi-select !rounded-[4px] border border-[#E3E3E3] flex-nowrap"
                    placeholder="Select Themes"
                    :searchable="false"
                    :indicator="false"
                    label="name"
                    :reduce="item => item.id"
                    multiple
                  />

                  <button
                    class="absolute right-[10px] top-[10px] !w-[20px] !h-[20px] hover:opacity-80 p-0"
                    type="button"
                    @click="themeModal = true"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.2606 2.8514L11.0411 5.0709L9.09961 4.37733L8.40608 2.43587L10.6256 0.216373C9.16938 -0.270789 7.49841 0.0644958 6.33886 1.22409C5.20071 2.36224 4.85681 3.99293 5.30499 5.42977C5.24463 5.47728 5.18615 5.52844 5.13046 5.58413L0.572138 10.1424C-0.190713 10.9052 -0.190713 12.142 0.572138 12.9048C1.33499 13.6677 2.57181 13.6677 3.33466 12.9048L7.89298 8.34652C7.94867 8.29083 7.99974 8.23239 8.04731 8.17204C9.48415 8.62021 11.1148 8.27627 12.2529 7.13812C13.4124 5.97852 13.7478 4.30765 13.2606 2.8514Z"
                        fill="#6E747A"
                      />
                    </svg>
                  </button>
                </div>
              </b-col>
            </b-row>

            <b-row class="w-full mt-10">
              <b-col>
                <label class="text-[15px] text-[#3E3E3E] mb-[12px]">
                  Who can access?
                </label>

                <v-select
                  v-model="form.access_level"
                  :options="access_levels"
                  class="multi-select !rounded-[4px] border border-[#E3E3E3] flex-nowrap"
                  :searchable="false"
                  :indicator="false"
                  label="text"
                  :reduce="item => item.value"
                />
              </b-col>
            </b-row>

            <template v-if="form.access_level === 'tags'">
              <b-row
                class="w-full mt-6"
              >
                <b-col>
                  <label class="text-[15px] text-[#3E3E3E] mb-[12px]">
                    Tags
                  </label>

                  <v-select
                    v-model="form.tags"
                    :options="tags"
                    class="multi-select !rounded-[4px] border border-[#E3E3E3] flex-nowrap"
                    :indicator="false"
                    label="name"
                    :reduce="item => item.value"
                    multiple
                  />
                </b-col>
              </b-row>

              <b-row class="w-full mt-6">
                <b-col>
                  <label class="text-[15px] text-[#3E3E3E] mb-[12px]">
                    Link Product
                  </label>

                  <v-select
                    v-model="form.product"
                    :options="products"
                    class="multi-select !rounded-[4px] border border-[#E3E3E3] flex-nowrap"
                    :searchable="false"
                    :indicator="false"
                    label="name"
                    :reduce="item => item.value"
                  />
                </b-col>
              </b-row>

              <b-row class="w-full mt-2">
                <b-col>
                  <b-checkbox
                    v-model="form.enable_coupons"
                    name="enable_coupons"
                    switch
                  >
                    <div class="text-wrap text-[#44474A] translate-y-[-.4rem] mb-0">
                      Enable Coupons
                    </div>
                  </b-checkbox>
                </b-col>
              </b-row>
            </template>

            <div
              v-if="!onlyAccess"
              class="flex flex-col gap-y-6 w-full mt-16 px-[15px]"
            >
              <p class="text-[15px] text-[#3E3E3E] mb-0">
                Pages <span class="text-[#ADADAD]">({{ form.pages.length }})</span>
              </p>

              <div class="flex flex-row gap-x-5 p-4 bg-[#FBFBFB] border border-[#E2E5EC] rounded-[5px] w-full overflow-x-auto scroll-x">
                <div
                  v-for="(page, index) in form.pages"
                  :key="index"
                  class="flex flex-col min-w-[256px] w-[256px] p-4 bg-white rounded-[10px] shadow-[0_2px_7px_rgba(0,0,0,0.07)]"
                >
                  <div
                    v-if="!page.thumbnail.source"
                    class="w-1/2 aspect-square object-cover bg-[#D9D9D9] border-[0.41px] border-[#E2E5EC] rounded-[5px] shadow-[0_0.82px_1.64px_rgba(0,0,0,0.05)]"
                  />
                  <img
                    v-else
                    class="w-1/2 aspect-square object-cover bg-[#D9D9D9] border-[0.41px] border-[#E2E5EC] rounded-[5px] shadow-[0_0.82px_1.64px_rgba(0,0,0,0.05)]"
                    alt="Page Thumbnail"
                    :src="page.thumbnail.source"
                  >

                  <p class="text-[14px] text-[#44474A] font-semibold mt-2 mb-0">
                    {{ page.name }}
                  </p>

                  <upload-asset
                    type="jpg|png|jpeg|blob|webp"
                    @uploaded="e => insertImage(e.url, index)"
                  >
                    <template #trigger>
                      <div class="inline-flex items-center mt-[12px]">
                        <label class="cursor-pointer inline-flex items-center">
                          <svg
                            class="mr-2 w-4 h-4"
                            width="14"
                            height="12"
                            viewBox="0 0 14 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2.16667 8.47463C1.46317 8.00373 1 7.20179 1 6.29167C1 4.92458 2.04505 3.80159 3.37985 3.67797C3.65289 2.01708 5.09514 0.75 6.83333 0.75C8.57152 0.75 10.0138 2.01708 10.2868 3.67797C11.6216 3.80159 12.6667 4.92458 12.6667 6.29167C12.6667 7.20179 12.2035 8.00373 11.5 8.47463M4.5 8.33333L6.83333 6M6.83333 6L9.16667 8.33333M6.83333 6V11.25"
                              stroke="black"
                              stroke-width="1.3"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>

                          <span class="text-[#44474A]">
                            Upload Thumbnail
                          </span>
                        </label>
                      </div>
                    </template>
                  </upload-asset>

                  <b-form-checkbox
                    v-model="page.visible"
                    name="visible"
                    class=""
                    switch
                  >
                    <div class="text-wrap text-[#44474A] translate-y-[-.4rem] mb-0">
                      Visible
                    </div>
                  </b-form-checkbox>
                </div>
              </div>
            </div>

            <div class="w-full flex flex-row gap-x-3 justify-start mt-10 px-[15px]">
              <template v-if="currentTemplate">
                <b-btn
                  variant="primary"
                  class="!bg-[#4F83E3] !inline-flex !items-center !justify-center hover:!opacity-80"
                  :disabled="loading"
                  @click="updateTemplate"
                >
                  <svg
                    class="mr-1 w-4 h-4"
                    width="14"
                    height="12"
                    viewBox="0 0 14 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.16667 8.47463C1.46317 8.00373 1 7.20179 1 6.29167C1 4.92458 2.04505 3.80159 3.37985 3.67797C3.65289 2.01708 5.09514 0.75 6.83333 0.75C8.57152 0.75 10.0138 2.01708 10.2868 3.67797C11.6216 3.80159 12.6667 4.92458 12.6667 6.29167C12.6667 7.20179 12.2035 8.00373 11.5 8.47463M4.5 8.33333L6.83333 6M6.83333 6L9.16667 8.33333M6.83333 6V11.25"
                      stroke="currentColor"
                      stroke-width="1.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  Update Template

                  <b-spinner
                    v-if="loading"
                    class="ml-3"
                    variant="light"
                    small
                  />
                </b-btn>

                <b-btn
                  variant="white"
                  class="!bg-white !border !border-[#CACACA] !text-[#4B4B4B] !inline-flex !items-center !justify-center hover:!opacity-60"
                  :disabled="loading"
                  @click="updateTemplateStatus(!currentTemplate.status)"
                >
                  {{ !currentTemplate.status ? 'Publish Template' : 'Unpublish Template' }}
                </b-btn>
              </template>

              <b-btn
                v-else
                variant="primary"
                class="!bg-[#4F83E3] !inline-flex !items-center !justify-center hover:!opacity-80"
                :disabled="loading"
                @click="uploadTemplate"
              >
                <svg
                  class="mr-1 w-4 h-4"
                  width="14"
                  height="12"
                  viewBox="0 0 14 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.16667 8.47463C1.46317 8.00373 1 7.20179 1 6.29167C1 4.92458 2.04505 3.80159 3.37985 3.67797C3.65289 2.01708 5.09514 0.75 6.83333 0.75C8.57152 0.75 10.0138 2.01708 10.2868 3.67797C11.6216 3.80159 12.6667 4.92458 12.6667 6.29167C12.6667 7.20179 12.2035 8.00373 11.5 8.47463M4.5 8.33333L6.83333 6M6.83333 6L9.16667 8.33333M6.83333 6V11.25"
                    stroke="currentColor"
                    stroke-width="1.3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                Upload Template

                <b-spinner
                  v-if="loading"
                  class="ml-3"
                  variant="light"
                  small
                />
              </b-btn>

              <b-btn
                variant="white"
                class="!bg-white !border !border-[#CACACA] !text-[#4B4B4B] !inline-flex !items-center !justify-center hover:!opacity-60"
                @click="modal = false"
              >
                Cancel
              </b-btn>
            </div>
          </template>

          <template v-else>
            <div class="w-full flex justify-center mt-2">
              <b-skeleton class="h-[28px] w-[220px]" />
            </div>

            <b-row class="w-full mt-10">
              <b-col>
                <b-skeleton class="h-4 w-40 mb-[12px]" />
                <b-skeleton class="h-[38px] w-full" />
              </b-col>
            </b-row>

            <div
              v-if="!onlyAccess && loading"
              class="flex flex-col gap-y-6 w-full mt-16 px-[15px]"
            >
              <b-skeleton class="h-5 w-24" />

              <div class="flex flex-row gap-x-5 p-4 bg-[#FBFBFB] border border-[#E2E5EC] rounded-[5px] w-full overflow-x-auto scroll-x">
                <div
                  v-for="n in 4"
                  :key="n"
                  class="flex flex-col min-w-[256px] w-[256px] p-4 bg-white rounded-[10px] shadow-[0_2px_7px_rgba(0,0,0,0.07)]"
                >
                  <b-skeleton class="w-1/2 aspect-square" />
                  <b-skeleton class="h-4 w-3/4 mt-3" />
                  <b-skeleton class="h-8 w-32 mt-3" />
                  <b-skeleton class="h-6 w-20 mt-3" />
                </div>
              </div>
            </div>
          </template>
        </b-form>
      </template>
    </b-modal>

    <b-modal
      v-model="categoryModal"
      dialog-class="!max-w-[640px] rounded-[5px]"
      body-class="!py-8 !px-16"
      hide-footer
      hide-header
      centered
      lazy
    >
      <i
        class="fa fa-close z-[50] absolute top-[15px] right-[17px] text-[16px] text-[#6E747A] transition cursor-pointer hover:opacity-60"
        @click="categoryModal = false"
      />

      <template>
        <div class="w-full flex flex-col items-center">
          <h4 class="text-center text-[27px] font-semibold">
            Manage Categories
          </h4>

          <div class="w-[400px] mx-auto mt-[24px]">
            <div class="w-full flex justify-center items-baseline gap-[10px]">
              <b-form-group
                label="New Category"
                class="w-full"
              >
                <div class="flex items-center gap-[10px]">
                  <b-input
                    v-model="newCategory"
                    name="name"
                    class="w-full"
                  />

                  <b-button
                    variant="primary"
                    @click="addTemplateCategory"
                  >
                    <svg
                      fill="none"
                      height="12"
                      viewBox="0 0 11 12"
                      width="11"
                      xmlns="http://www.w3.org/2000/svg"
                    ><path
                      d="M6.808 11.815H4.807V7.146H0.667V5.329H4.807V0.843999H6.808V5.329H10.925V7.146H6.808V11.815Z"
                      fill="white"
                    /></svg>
                  </b-button>
                </div>
              </b-form-group>
            </div>

            <div class="w-full mt-[16px]">
              <ul class="list-none">
                <li
                  v-for="(category, index) in categories"
                  :key="index"
                >
                  <b-form-group class="w-full">
                    <div class="flex items-center gap-[10px]">
                      <b-input
                        v-model="category.name"
                        type="text"
                        class="w-full border h-[32px] px-[8px]"
                      />
                      <button
                        class="w-[32px] h-[32px] p-0"
                        @click="updateTemplateCategory(category)"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="mr-2"
                        ><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M10.8048 0.528575C10.5444 0.268226 10.1223 0.268226 9.86197 0.528575L0.528636 9.86191C0.403612 9.98693 0.333374 10.1565 0.333374 10.3333V13C0.333374 13.3682 0.631851 13.6666 1.00004 13.6666H3.66671C3.84352 13.6666 4.01309 13.5964 4.13811 13.4714L13.4714 4.13805C13.7318 3.8777 13.7318 3.45559 13.4714 3.19524L10.8048 0.528575ZM11 4.72384L9.27619 2.99998L10.3334 1.94279L12.0572 3.66665L11 4.72384ZM8.33338 3.94279L10.0572 5.66665L4.33337 11.3905L2.60952 9.66665L8.33338 3.94279ZM1.66671 10.6095L3.39057 12.3333H1.66671V10.6095Z"
                          fill="#44474A"
                        /><path
                          d="M7.66671 12.3333C7.29852 12.3333 7.00004 12.6318 7.00004 13C7.00004 13.3682 7.29852 13.6666 7.66671 13.6666H13C13.3682 13.6666 13.6667 13.3682 13.6667 13C13.6667 12.6318 13.3682 12.3333 13 12.3333H7.66671Z"
                          fill="#44474A"
                        /></svg>
                      </button>
                      <button
                        class="w-[32px] h-[32px] p-0"
                        @click="removeTemplateCategory(category)"
                      >
                        <svg
                          width="14"
                          height="18"
                          viewBox="0 0 14 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="mr-2 w-4 h-4"
                        ><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M5.5 0.75C5.08579 0.75 4.75 1.08579 4.75 1.5V3H1C0.585786 3 0.25 3.33579 0.25 3.75C0.25 4.16421 0.585786 4.5 1 4.5H13C13.4142 4.5 13.75 4.16421 13.75 3.75C13.75 3.33579 13.4142 3 13 3H9.25V1.5C9.25 1.08579 8.91421 0.75 8.5 0.75H5.5ZM7.75 3H6.25V2.25H7.75V3Z"
                          fill="#44474A"
                        /><path
                          d="M5.5 8.25C5.91421 8.25 6.25 8.58579 6.25 9V13.5C6.25 13.9142 5.91421 14.25 5.5 14.25C5.08579 14.25 4.75 13.9142 4.75 13.5V9C4.75 8.58579 5.08579 8.25 5.5 8.25Z"
                          fill="#44474A"
                        /><path
                          d="M9.25 9C9.25 8.58579 8.91421 8.25 8.5 8.25C8.08579 8.25 7.75 8.58579 7.75 9V13.5C7.75 13.9142 8.08579 14.25 8.5 14.25C8.91421 14.25 9.25 13.9142 9.25 13.5V9Z"
                          fill="#44474A"
                        /><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.00001 5.25C0.782586 5.25 0.575867 5.34435 0.433415 5.5086C0.290961 5.67285 0.226795 5.89083 0.257543 6.10607L1.57356 15.3182C1.73191 16.4267 2.68123 17.25 3.80095 17.25H10.1991C11.3188 17.25 12.2681 16.4267 12.4264 15.3182L13.7425 6.10607C13.7732 5.89083 13.709 5.67285 13.5666 5.5086C13.4241 5.34435 13.2174 5.25 13 5.25H1.00001ZM3.05849 15.1061L1.86476 6.75H12.1352L10.9415 15.1061C10.8887 15.4756 10.5723 15.75 10.1991 15.75H3.80095C3.42771 15.75 3.11127 15.4756 3.05849 15.1061Z"
                          fill="#44474A"
                        /><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M5.5 0.75C5.08579 0.75 4.75 1.08579 4.75 1.5V3H1C0.585786 3 0.25 3.33579 0.25 3.75C0.25 4.16421 0.585786 4.5 1 4.5H13C13.4142 4.5 13.75 4.16421 13.75 3.75C13.75 3.33579 13.4142 3 13 3H9.25V1.5C9.25 1.08579 8.91421 0.75 8.5 0.75H5.5ZM7.75 3H6.25V2.25H7.75V3Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /><path
                          d="M5.5 8.25C5.91421 8.25 6.25 8.58579 6.25 9V13.5C6.25 13.9142 5.91421 14.25 5.5 14.25C5.08579 14.25 4.75 13.9142 4.75 13.5V9C4.75 8.58579 5.08579 8.25 5.5 8.25Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /><path
                          d="M9.25 9C9.25 8.58579 8.91421 8.25 8.5 8.25C8.08579 8.25 7.75 8.58579 7.75 9V13.5C7.75 13.9142 8.08579 14.25 8.5 14.25C8.91421 14.25 9.25 13.9142 9.25 13.5V9Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.00001 5.25C0.782586 5.25 0.575867 5.34435 0.433415 5.5086C0.290961 5.67285 0.226795 5.89083 0.257543 6.10607L1.57356 15.3182C1.73191 16.4267 2.68123 17.25 3.80095 17.25H10.1991C11.3188 17.25 12.2681 16.4267 12.4264 15.3182L13.7425 6.10607C13.7732 5.89083 13.709 5.67285 13.5666 5.5086C13.4241 5.34435 13.2174 5.25 13 5.25H1.00001ZM3.05849 15.1061L1.86476 6.75H12.1352L10.9415 15.1061C10.8887 15.4756 10.5723 15.75 10.1991 15.75H3.80095C3.42771 15.75 3.11127 15.4756 3.05849 15.1061Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /></svg>
                      </button>
                    </div>
                  </b-form-group>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <b-modal
      v-model="themeModal"
      dialog-class="!max-w-[640px] rounded-[5px]"
      body-class="!py-8 !px-16"
      hide-footer
      hide-header
      centered
      lazy
    >
      <i
        class="fa fa-close z-[50] absolute top-[15px] right-[17px] text-[16px] text-[#6E747A] transition cursor-pointer hover:opacity-60"
        @click="themeModal = false"
      />

      <template>
        <div class="w-full flex flex-col items-center">
          <h4 class="text-center text-[27px] font-semibold">
            Manage Themes
          </h4>

          <div class="w-[400px] mx-auto mt-[24px]">
            <div class="w-full flex justify-center items-baseline gap-[10px]">
              <b-form-group
                label="New Theme"
                class="w-full"
              >
                <div class="flex items-center gap-[10px]">
                  <b-input
                    v-model="newTheme"
                    name="name"
                    class="w-full"
                  />

                  <b-button
                    variant="primary"
                    @click="addTemplateTheme"
                  >
                    <svg
                      fill="none"
                      height="12"
                      viewBox="0 0 11 12"
                      width="11"
                      xmlns="http://www.w3.org/2000/svg"
                    ><path
                      d="M6.808 11.815H4.807V7.146H0.667V5.329H4.807V0.843999H6.808V5.329H10.925V7.146H6.808V11.815Z"
                      fill="white"
                    /></svg>
                  </b-button>
                </div>
              </b-form-group>
            </div>

            <div class="w-full mt-[16px]">
              <ul class="list-none">
                <li
                  v-for="(theme, index) in themes"
                  :key="index"
                >
                  <b-form-group class="w-full">
                    <div class="flex items-center gap-[10px]">
                      <b-input
                        v-model="theme.name"
                        type="text"
                        class="w-full border h-[32px] px-[8px]"
                      />
                      <button
                        class="w-[32px] h-[32px] p-0"
                        @click="updateTemplateTheme(theme)"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="mr-2"
                        ><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M10.8048 0.528575C10.5444 0.268226 10.1223 0.268226 9.86197 0.528575L0.528636 9.86191C0.403612 9.98693 0.333374 10.1565 0.333374 10.3333V13C0.333374 13.3682 0.631851 13.6666 1.00004 13.6666H3.66671C3.84352 13.6666 4.01309 13.5964 4.13811 13.4714L13.4714 4.13805C13.7318 3.8777 13.7318 3.45559 13.4714 3.19524L10.8048 0.528575ZM11 4.72384L9.27619 2.99998L10.3334 1.94279L12.0572 3.66665L11 4.72384ZM8.33338 3.94279L10.0572 5.66665L4.33337 11.3905L2.60952 9.66665L8.33338 3.94279ZM1.66671 10.6095L3.39057 12.3333H1.66671V10.6095Z"
                          fill="#44474A"
                        /><path
                          d="M7.66671 12.3333C7.29852 12.3333 7.00004 12.6318 7.00004 13C7.00004 13.3682 7.29852 13.6666 7.66671 13.6666H13C13.3682 13.6666 13.6667 13.3682 13.6667 13C13.6667 12.6318 13.3682 12.3333 13 12.3333H7.66671Z"
                          fill="#44474A"
                        /></svg>
                      </button>
                      <button
                        class="w-[32px] h-[32px] p-0"
                        @click="removeTemplateTheme(theme)"
                      >
                        <svg
                          width="14"
                          height="18"
                          viewBox="0 0 14 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          class="mr-2 w-4 h-4"
                        ><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M5.5 0.75C5.08579 0.75 4.75 1.08579 4.75 1.5V3H1C0.585786 3 0.25 3.33579 0.25 3.75C0.25 4.16421 0.585786 4.5 1 4.5H13C13.4142 4.5 13.75 4.16421 13.75 3.75C13.75 3.33579 13.4142 3 13 3H9.25V1.5C9.25 1.08579 8.91421 0.75 8.5 0.75H5.5ZM7.75 3H6.25V2.25H7.75V3Z"
                          fill="#44474A"
                        /><path
                          d="M5.5 8.25C5.91421 8.25 6.25 8.58579 6.25 9V13.5C6.25 13.9142 5.91421 14.25 5.5 14.25C5.08579 14.25 4.75 13.9142 4.75 13.5V9C4.75 8.58579 5.08579 8.25 5.5 8.25Z"
                          fill="#44474A"
                        /><path
                          d="M9.25 9C9.25 8.58579 8.91421 8.25 8.5 8.25C8.08579 8.25 7.75 8.58579 7.75 9V13.5C7.75 13.9142 8.08579 14.25 8.5 14.25C8.91421 14.25 9.25 13.9142 9.25 13.5V9Z"
                          fill="#44474A"
                        /><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.00001 5.25C0.782586 5.25 0.575867 5.34435 0.433415 5.5086C0.290961 5.67285 0.226795 5.89083 0.257543 6.10607L1.57356 15.3182C1.73191 16.4267 2.68123 17.25 3.80095 17.25H10.1991C11.3188 17.25 12.2681 16.4267 12.4264 15.3182L13.7425 6.10607C13.7732 5.89083 13.709 5.67285 13.5666 5.5086C13.4241 5.34435 13.2174 5.25 13 5.25H1.00001ZM3.05849 15.1061L1.86476 6.75H12.1352L10.9415 15.1061C10.8887 15.4756 10.5723 15.75 10.1991 15.75H3.80095C3.42771 15.75 3.11127 15.4756 3.05849 15.1061Z"
                          fill="#44474A"
                        /><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M5.5 0.75C5.08579 0.75 4.75 1.08579 4.75 1.5V3H1C0.585786 3 0.25 3.33579 0.25 3.75C0.25 4.16421 0.585786 4.5 1 4.5H13C13.4142 4.5 13.75 4.16421 13.75 3.75C13.75 3.33579 13.4142 3 13 3H9.25V1.5C9.25 1.08579 8.91421 0.75 8.5 0.75H5.5ZM7.75 3H6.25V2.25H7.75V3Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /><path
                          d="M5.5 8.25C5.91421 8.25 6.25 8.58579 6.25 9V13.5C6.25 13.9142 5.91421 14.25 5.5 14.25C5.08579 14.25 4.75 13.9142 4.75 13.5V9C4.75 8.58579 5.08579 8.25 5.5 8.25Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /><path
                          d="M9.25 9C9.25 8.58579 8.91421 8.25 8.5 8.25C8.08579 8.25 7.75 8.58579 7.75 9V13.5C7.75 13.9142 8.08579 14.25 8.5 14.25C8.91421 14.25 9.25 13.9142 9.25 13.5V9Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /><path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M1.00001 5.25C0.782586 5.25 0.575867 5.34435 0.433415 5.5086C0.290961 5.67285 0.226795 5.89083 0.257543 6.10607L1.57356 15.3182C1.73191 16.4267 2.68123 17.25 3.80095 17.25H10.1991C11.3188 17.25 12.2681 16.4267 12.4264 15.3182L13.7425 6.10607C13.7732 5.89083 13.709 5.67285 13.5666 5.5086C13.4241 5.34435 13.2174 5.25 13 5.25H1.00001ZM3.05849 15.1061L1.86476 6.75H12.1352L10.9415 15.1061C10.8887 15.4756 10.5723 15.75 10.1991 15.75H3.80095C3.42771 15.75 3.11127 15.4756 3.05849 15.1061Z"
                          stroke="white"
                          stroke-width="0.2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        /></svg>
                      </button>
                    </div>
                  </b-form-group>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import UploadAsset
  from '@/components/editor/components/sid-controls/sid-control-pressets/background-image/UploadAsset.vue'

export default {
  name: 'UploadAsTemplate',
  components: {UploadAsset},
  props: {
    project: {
      type: Object,
      required: true
    },
    onlyAccess: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      modal: false,
      tags: [],
      products: [],
      pages: [],
      subdomain: '',
      categoryModal: false,
      themeModal: false,
      newCategory: '',
      newTheme: '',
      uploaded: false,
      currentTemplate: null,
      access_levels: [
        {
          text: 'All users have access',
          value: 'all'
        },
        {
          text: 'Only users with these tags',
          value: 'tags'
        }
      ],
      access_restrict_options: [
        {
          text: 'Redirect to page',
          value: 'page'
        },
        {
          text: 'Show product purchase modal',
          value: 'purchase'
        }
      ],
      priceTypes: [
        {
          text: 'Paid',
          value: 'paid'
        },
        {
          text: 'Free',
          value: 'free'
        }
      ],
      themes: [],
      categories: [],
      form: {
        thumbnail: {
          default: true,
          screenshot: null,
          source: null,
          file: null
        },
        name: '',
        description: '',
        categories: [],
        themes: [],
        tags: [],
        product: null,
        redirect: {
          name: 'Custom Link',
          type: 'custom',
          url: ''
        },
        enable_coupons: false,
        access_level: 'all',
        restricted_action: 'page',
        price: {
          type: 'free',
          amount: 0
        },
        pages: []
      }
    }
  },
  methods: {
    formatUrl (val) {
      if (val.startsWith('http')) {
        this.form.redirect.url = val
      } else {
        this.form.redirect.url = `https://${val}`
      }
    },
    async setForm () {
      this.loading = true

      const {data: templates} = await axios.get(`api/admin/templates?project_id=${this.project.id}`)
      let pagesTMP = []

      if (templates.data.data.length) {
        const {data: template} = await axios.get(`api/admin/templates/${templates.data.data[0].id}`)
        this.currentTemplate = template.data
        this.form.name = template.data.name
        this.form.description = template.data.description
        this.form.thumbnail.default = false
        this.form.thumbnail.source = template.data.cover_url || template.data.cover
        this.form.price.type = template.data.is_free ? 'free' : 'paid'
        this.form.price.amount = template.data.amount
        this.form.categories = template.data.categories_rel.map(category => category.category_id)
        this.form.themes = template.data.themes.map(theme => theme.theme_id)
        this.form.access_level = template.data.access_level
        this.form.product = template.data.product
        this.form.enable_coupons = template.data.enable_coupons
        this.subdomain = template.data.view_link.split('.')[0].split('//')[1]
        pagesTMP = template.data.pages.map(page => {
          return {
            id: page.project_id,
            name: page.name,
            visible: true,
            thumbnail: {
              default: false,
              source: page.cover_url || page.cover,
              file: null
            }
          }
        })

        if (template.data.tags?.length > 0) {
          this.form.tags = template.data.tags.map(tag => Number(tag))
        }
      } else {
        this.form.name = this.project.name
        this.form.description = this.project.description
      }

      const fetchUrl = this.onlyAccess ? `api/site/${this.subdomain}` : `api/projects/${this.project.id}`

      const {data: project} = await axios.get(fetchUrl)
      this.form.content = project.data.content
      this.form.global_settings = project.data.global_settings
      this.form.project_hash = project.data.hash
      this.form.setup = project.data.setup
      this.form.view_link = `https://${project.data.subdomain}.estage.site`
      this.form.status = true

      this.form.thumbnail.screenshot = this.project.screenshot
      const pagesTMPIds = pagesTMP.map(page => page.id)

      const {data: pages} = await axios.get(`api/projects/${this.project.id}/pages?per_page=100`)
      this.form.pages = [
        ...pagesTMP,
        ...pages.data.data
          .filter(page => !pagesTMPIds.includes(page.id))
          .map(page => {
            return {
              id: page.id,
              name: page.title,
              visible: false,
              thumbnail: {
                default: false,
                source: '',
                file: null
              }
            }
          })
      ]

      this.loading = false
    },
    async urlToFile (url, filename, mimeType) {
      const response = await fetch(url)
      const blob = await response.blob()
      return new File([blob], filename, { type: mimeType })
    },
    convertFileToBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
    async uploadImage (file, page) {
      const image = file.target.files[0]
      if (image) {
        const reader = new FileReader()
        reader.onload = (e) => {
          if (page >= 0) {
            this.form.pages[page].thumbnail.file = image
            this.form.pages[page].thumbnail.source = e.target.result
          } else {
            this.form.thumbnail.file = image
            this.form.thumbnail.source = e.target.result
            this.form.thumbnail.default = false
          }
        }
        reader.readAsDataURL(image)
      }
    },
    insertImage (file, page) {
      if (!file) return

      if (page >= 0) {
        this.form.pages[page].thumbnail.source = file
      } else {
        this.form.thumbnail.source = file
      }
    },
    async uploadTemplate () {
      this.loading = true
      const formData = new FormData()

      let coverFile
      if (this.form.thumbnail.default) {
        coverFile = this.form.thumbnail.screenshot
      } else {
        coverFile = this.form.thumbnail.source
      }

      // formData.append('is_free', this.form.price.type === 'free' ? 1 : 0)
      // formData.append('amount', this.form.price.amount)
      formData.append('name', this.form.name)
      formData.append('description', this.form.description || '')
      formData.append('project_id', this.project.id)
      formData.append('status', this.form.status ? 1 : 0)
      formData.append('global_settings', JSON.stringify(this.form.global_settings))
      formData.append('project_hash', this.form.project_hash)
      formData.append('access_level', this.form.access_level)

      if (!this.onlyAccess) {
        formData.append('cover', coverFile)
        formData.append('content', this.form.content)
        formData.append('setup', this.form.setup || '')
        formData.append('view_link', this.form.view_link || '')

        this.form.categories.forEach(category => formData.append('categories[]', category))
        this.form.themes.forEach(theme => formData.append('themes[]', theme))
        this.form.pages
          .filter(page => page.visible)
          .forEach((page, index) => {
            formData.append(`pages[${index}][project_id]`, page.id)
            formData.append(`pages[${index}][name]`, page.name)
            formData.append(`pages[${index}][cover]`, page.thumbnail.source)
          })
      }

      if (this.form.access_level === 'tags') {
        this.form.tags.forEach(tag => formData.append('tags[]', tag))
        formData.append('enable_coupons', this.form.enable_coupons ? 1 : 0)
        formData.append('product', this.form.product)
      }

      try {
        const { data } = await axios.post('api/admin/templates', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        if (data.status === 'success') this.currentTemplate = data.data
        this.modal = false
        this.$swal({
          icon: 'success',
          title: 'Done!',
          text: 'Post published.',
          showConfirmButton: false,
          timer: 1500
        })
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: 'Error!',
          text: error.message || 'Template publishing failed.',
          showConfirmButton: false,
          timer: 1500
        })
      } finally {
        this.loading = false
      }
    },
    async updateTemplate () {
      this.loading = true

      const payload = {
        // is_free: this.form.price.type === 'free' ? 1 : 0,
        // amount: this.form.price.amount,
        name: this.form.name,
        description: this.form.description || '',
        project_id: this.project.id,
        status: this.form.status ? 1 : 0,
        global_settings: this.form.global_settings,
        project_hash: this.form.project_hash,
        access_level: this.form.access_level
      }

      if (!this.onlyAccess) {
        payload.cover = this.form.thumbnail.default ? this.form.thumbnail.screenshot : this.form.thumbnail.source
        payload.content = this.form.content
        payload.setup = this.form.setup ?? null
        payload.view_link = this.form.view_link || ''
        payload.categories = this.form.categories
        payload.themes = this.form.themes
        payload.pages = this.form.pages
          .filter(page => page.visible)
          .map(page => ({
            project_id: page.id,
            name: page.name,
            cover: page.thumbnail.source
          }))
      }

      if (this.form.access_level === 'all') {
        payload.tags = null
        payload.enable_coupons = null
        payload.product = null
      } else if (this.form.access_level === 'tags') {
        payload.tags = this.form.tags
        payload.enable_coupons = this.form.enable_coupons ? 1 : 0
        payload.product = this.form.product
      }

      try {
        const { data } = await axios.put(`api/admin/templates/${this.currentTemplate.id}`, payload)
        if (data.status === 'success') this.currentTemplate = data.data
        this.modal = false
        this.$swal({
          icon: 'success',
          title: 'Done!',
          text: 'Post updated.',
          showConfirmButton: false,
          timer: 1500
        })
        this.$emit('update')
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: 'Error!',
          text: error.message || 'Template updating failed.',
          showConfirmButton: false,
          timer: 1500
        })
      } finally {
        this.loading = false
      }
    },
    async updateTemplateStatus (status) {
      await axios.put(`/api/admin/templates/status/${this.currentTemplate.id}`, {status: status})
        .then(({data}) => {
          this.currentTemplate = data.data
          this.modal = false
          this.$swal({
            icon: 'success',
            title: 'Done!',
            text: status ? 'Post published.' : 'Post unpublished.',
            showConfirmButton: false,
            timer: 1500
          })
        })
        .catch((error) => {
          this.$swal({
            icon: 'error',
            title: 'Error!',
            text: error.message || status ? 'Template publishing failed.' : 'Template unpublishing failed.',
            showConfirmButton: false,
            timer: 1500
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async addTemplateCategory () {
      await axios.post('api/admin/templates/categories', {
        name: this.newCategory
      })
        .then(async () => {
          this.$swal({
            icon: 'success',
            title: 'Category added',
            text: 'Category added successfully',
            showConfirmButton: false,
            timer: 1500
          })

          await axios.get('api/templates/categories')
            .then(({data}) => {
              this.categories = data.data
            })
        })
        .finally(() => {
          this.newCategory = ''
        })
    },
    async addTemplateTheme () {
      await axios.post('api/admin/templates/themes', {
        name: this.newTheme,
        is_active: true
      })
        .then(async () => {
          this.$swal({
            icon: 'success',
            title: 'Theme added',
            text: 'Theme added successfully',
            showConfirmButton: false,
            timer: 1500
          })

          await axios.get('api/templates/themes')
            .then(({data}) => {
              this.themes = data.data
            })
        })
        .finally(() => {
          this.newTheme = ''
        })
    },
    async updateTemplateCategory (category) {
      await axios.put(`api/admin/templates/categories/${category.id}`, {
        name: category.name
      })
        .then(async () => {
          this.$swal({
            icon: 'success',
            title: 'Category updated',
            text: 'Category updated successfully',
            showConfirmButton: false,
            timer: 1500
          })

          await axios.get('api/templates/categories')
            .then(({data}) => {
              this.categories = data.data
            })
        })
    },
    async updateTemplateTheme (theme) {
      await axios.put(`api/admin/templates/themes/${theme.id}`, {
        name: theme.name,
        is_active: true
      })
        .then(async () => {
          this.$swal({
            icon: 'success',
            title: 'Theme updated',
            text: 'Theme updated successfully',
            showConfirmButton: false,
            timer: 1500
          })

          await axios.get('api/templates/themes')
            .then(({data}) => {
              this.themes = data.data
            })
        })
    },
    async removeTemplateCategory (category) {
      await axios.delete(`api/admin/templates/categories/${category.id}`)
        .then(async () => {
          this.$swal({
            icon: 'success',
            title: 'Category removed',
            text: 'Category removed successfully',
            showConfirmButton: false,
            timer: 1500
          })

          await axios.get('api/templates/categories')
            .then(({data}) => {
              this.categories = data.data
            })
        })
    },
    async removeTemplateTheme (theme) {
      await axios.delete(`api/admin/templates/themes/${theme.id}`)
        .then(async () => {
          this.$swal({
            icon: 'success',
            title: 'Theme removed',
            text: 'Theme removed successfully',
            showConfirmButton: false,
            timer: 1500
          })

          await axios.get('api/templates/themes')
            .then(({data}) => {
              this.themes = data.data
            })
        })
    }
  },
  watch: {
    async modal (val) {
      if (!val) return
      this.loading = true
      const estageID = '38804'

      try {
        const reqs = []
        if (!this.onlyAccess) {
          reqs.push(axios.get('api/templates/categories'))
          reqs.push(axios.get('api/templates/themes'))
        } else {
          reqs.push(Promise.resolve(null))
          reqs.push(Promise.resolve(null))
        }

        const tagsReq = axios.get(`api/user/payment-systems/products/tags?project_id=${estageID}&per_page=1000`)
        const productsReq = axios.get(`api/user/payment-systems/products?project_id=${estageID}&per_page=1000`)

        const [categoriesRes, themesRes, tagsRes, productsRes] = await Promise.all([
          ...reqs,
          tagsReq,
          productsReq
        ])

        if (!this.onlyAccess) {
          this.categories = categoriesRes?.data?.data || []
          this.themes = themesRes?.data?.data || []
        }

        this.tags = (tagsRes?.data?.data?.data || []).map(item => ({ name: item.name, value: item.id }))
        this.products = (productsRes?.data?.data?.data || []).map(item => ({ name: item.name, value: item.id }))

        await this.setForm()
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>